// packages/ui/src/components/LightTooltip.tsx
'use client';

/**
 * MUI Tooltip with its defaults, for one child that takes a ref and the pointer, focus and touch handlers, without
 * Popper.js (docs/embed-size-and-speed.md, "popper": in the terms and reviews widgets, MUI Tooltip was the only
 * reason for Popper, about 31–37 KB). It behaves as MUI's does:
 *   - hover opens it after 100 ms (at once, within 800 ms of a tooltip closing), keyboard focus too (`:focus-visible`
 *     only), a touch held for 700 ms then 100 ms; leaving, blurring or Escape closes it, a touch 1.5 s after it ends;
 *     pointer events right after a touch are ignored, as MUI ignores them;
 *   - the pointer may move onto it without closing it (MUI's interactive default);
 *   - MUI's look, read from the theme: grey 700 at 0.92, white 11 px medium text (14 px regular after a touch), its
 *     padding, radius and 14 px gap (24 px after a touch), and the Grow transition's timings;
 *   - drawn where Popper draws it (`placeLightTooltip`), in a layer portaled to the page's body at the theme's tooltip
 *     z-index, which follows the anchor when the page scrolls or resizes;
 *   - `role="tooltip"`, and the title as the child's `aria-label` unless the child has its own (MUI's
 *     `describeChild={false}`).
 * The look is inline styles; what MUI's tooltip inherits (line height, letter spacing) it inherits the same way, and
 * the layer carries `MuiPopper-root`, which the widgets' scoped base styles (`widgetScopedBaseline`) cover like MUI's
 * own layers. Only `title` is supported: no placement, arrow, delays or controlled `open`.
 *
 * The tooltip itself is `LightTooltipBase`, given the theme: the terms widget, which has no MUI, gives it a stand-in
 * for MUI's default theme.
 */
import { useTheme } from '@mui/material/styles';

import { LightTooltipBase, type LightTooltipProps } from './LightTooltipBase';

export type { LightTooltipProps };

export function LightTooltip(props: LightTooltipProps) {
  const theme = useTheme();
  return <LightTooltipBase {...props} theme={theme} />;
}
